import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Star, Heart, Trophy, Sparkles, Activity, MapPin, Quote, Check, Play, Zap, Flower2, Leaf, Baby, Clock, Wind, Droplet
} from "lucide-react";
import { api } from "../lib/api";
import { LogoWhite } from "../components/Logo";
import TimerAnimation from "../components/TimerAnimation";

// Real assets from client's site (form-elec.fr)
const ROSA_AVATAR = "https://form-elec.fr/wp-content/uploads/2024/01/rosa-formelec-avatar-1.png";
const IMG_REMISE = "https://form-elec.fr/wp-content/uploads/2024/01/Perte-de-poids-minceur-istres.png";
const IMG_SANTE = "https://form-elec.fr/wp-content/uploads/2024/01/fitness-tonification-sport-istres.png";
const IMG_SPORT = "https://form-elec.fr/wp-content/uploads/2024/01/Remise-en-forme-istres.png";
const IMG_EMS_HOME = "https://form-elec.fr/wp-content/uploads/2024/01/electrostimulation-sante-bien-etre-fos-sur-mer-q25kwop8e24vvlfsu9aonanwfwreexwzrrto7r7n4e.jpg";
const IMG_EMS_SPORT = "https://form-elec.fr/wp-content/uploads/2024/01/electrostimulation-sport-istres.jpg";
const SAP_LOGO = "https://form-elec.fr/wp-content/uploads/2024/01/logosapgrandombre-1024x754.png";

const BENEFITS = [
  {
    icon: Activity,
    title: "Remise en forme",
    universe: "EMS",
    subtitle: "Sculpte la silhouette & tonifie le corps",
    image: IMG_REMISE,
    points: [
      "Jusqu'à 4% de graisses en moins après 6 séances (femmes)",
      "Réduction de la corpulence de 2,3 cm (hommes)",
      "Seniors 65+ : 6% de graisse en moins après 14 semaines",
    ],
  },
  {
    icon: Droplet,
    title: "Détente & Détox",
    universe: "Massages",
    subtitle: "Drainage lymphatique & jambes légères",
    image: "https://images.pexels.com/photos/3997991/pexels-photo-3997991.jpeg?auto=compress&cs=tinysrgb&w=1200",
    points: [
      "Élimination toxines & rétention d'eau",
      "Effet jambes légères dès la première séance",
      "Soulagement œdèmes grossesse & post-opératoire",
    ],
  },
  {
    icon: Trophy,
    title: "Sport & Performance",
    universe: "EMS",
    subtitle: "Force, endurance, explosivité",
    image: IMG_SPORT,
    points: [
      "+8 à 9% de force max isométrique après 4 séances",
      "+17% en moyenne de force maximale dynamique",
      "Performances générales en hausse chez 84% des sujets",
    ],
  },
  {
    icon: Leaf,
    title: "Bien-être & Récupération",
    universe: "Massages",
    subtitle: "Massage récupération & palper-rouler",
    image: "https://images.pexels.com/photos/4498604/pexels-photo-4498604.jpeg?auto=compress&cs=tinysrgb&w=1200",
    points: [
      "Diminution des courbatures post-effort",
      "Idéal en complément d'une séance EMS",
      "Anti-cellulite ciblé : ventre, hanches, cuisses",
    ],
  },
];

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);
  const [showVideo, setShowVideo] = useState(false);
  const [packs, setPacks] = useState([]);

  useEffect(() => {
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/packs").then((r) => setPacks(r.data)).catch(() => {});
  }, []);

  const priceOf = (id) => {
    const p = packs.find((x) => x.id === id);
    return p ? `${Number(p.price).toLocaleString("fr-FR")}€` : "";
  };

  return (
    <div className="bg-white">
      {/* HERO GRAY */}
      <section className="hero-gray text-white relative overflow-hidden" data-testid="hero-section">
        {/* Background video — YouTube autoplay muted loop */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {/* fallback gradient under video while loading */}
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #5C5C5C 0%, #4F4F4F 100%)" }} />
          <iframe
            src="https://www.youtube-nocookie.com/embed/RC51sAcpjFE?autoplay=1&mute=1&loop=1&playlist=RC51sAcpjFE&controls=0&modestbranding=1&showinfo=0&rel=0&iv_load_policy=3&playsinline=1&disablekb=1&fs=0&cc_load_policy=0"
            title="FORM'elec — Ambiance"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen={false}
            tabIndex={-1}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] min-w-[100vw] h-[56.25vw] min-h-[100%] pointer-events-none"
            style={{ border: 0, scale: "1.15" }}
          />
          {/* Dark overlay for readability */}
          <div className="absolute inset-0 bg-fegray-dark/60" />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-6 lg:px-10 pt-14 sm:pt-16 lg:pt-20 pb-32 sm:pb-40 lg:pb-48">
          {/* Logo white+green */}
          <div className="reveal flex flex-col items-center text-center" style={{ animationDelay: "0.1s" }}>
            <LogoWhite size="xl" />
          </div>

          {/* Main headline */}
          <h1 className="reveal mt-10 sm:mt-12 lg:mt-14 text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.05] text-center" style={{ animationDelay: "0.25s" }}>
            <span className="text-fegreen">Stimulez</span>{" "}
            <span className="text-white font-light">votre</span>
            <br className="hidden sm:block" />
            <span className="text-[#E6CBAE] font-bold"> bien-être !</span>
          </h1>

          <p className="reveal mt-5 sm:mt-7 text-sm sm:text-base lg:text-lg text-white/85 max-w-2xl mx-auto px-4 text-center" style={{ animationDelay: "0.35s" }}>
            Coaching <strong className="text-fegreen-light">EMS</strong> et soins <strong className="text-[#E6CBAE]">massages</strong> à domicile —
            Istres &amp; alentours · Avance immédiate URSSAF 50%.
          </p>

          {/* Twin pillars — EMS vs Massages */}
          <div className="reveal mt-10 sm:mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 max-w-4xl mx-auto" style={{ animationDelay: "0.5s" }}>
            <Pillar
              accent="#84C342"
              accentSoft="rgba(132,195,66,0.18)"
              icon={Zap}
              tag="Univers 01"
              title="EMS à domicile"
              tagline="25 min = 4 h de sport"
              bullets={["Technologie MIHA BODYTEC", "Tonification globale", "Zéro impact articulaire"]}
              cta="Découvrir l'EMS"
              ctaLink="/tarifs"
              testid="hero-pillar-ems"
            />
            <Pillar
              accent="#C49B7C"
              accentSoft="rgba(196,155,124,0.22)"
              icon={Leaf}
              tag="Univers 02"
              title="Massages bien-être"
              tagline="Drainer · Sculpter · Apaiser"
              bullets={["Drainage lymphatique", "Drain & Sculpt corps", "Post-opératoire & prénatal"]}
              cta="Découvrir les soins"
              ctaLink="/univers/massages"
              testid="hero-pillar-massage"
            />
          </div>

          <div className="reveal mt-10 sm:mt-12 flex flex-wrap justify-center gap-3" style={{ animationDelay: "0.65s" }}>
            <Link to="/reservation" className="btn-green text-sm sm:text-base" data-testid="hero-cta-btn">
              Réserver ma séance <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              onClick={() => setShowVideo(true)}
              className="btn-outline-green !text-white !border-white hover:!bg-white hover:!text-fegreen"
              data-testid="hero-video-btn"
            >
              <Play className="w-4 h-4 fill-current" /> Voir la vidéo
            </button>
          </div>
        </div>

        {/* Wave separator — sits ABOVE the video for a clean transition */}
        <svg
          className="wave-separator"
          viewBox="0 0 1440 110"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          {/* Soft shadow above the wave */}
          <path
            d="M0,50 C320,110 640,5 960,50 C1200,80 1320,90 1440,55 L1440,110 L0,110 Z"
            fill="rgba(0,0,0,0.18)"
            transform="translate(0,-6)"
          />
          {/* Light back wave */}
          <path
            d="M0,55 C320,115 640,10 960,55 C1200,85 1320,90 1440,60 L1440,110 L0,110 Z"
            fill="#F5F5F5"
          />
          {/* Front white wave (matches next section bg) */}
          <path
            d="M0,75 C320,125 640,30 960,75 C1200,100 1320,100 1440,80 L1440,110 L0,110 Z"
            fill="#ffffff"
          />
        </svg>
      </section>

      {/* Video modal */}
      {showVideo && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setShowVideo(false)}
          data-testid="video-modal"
        >
          <div className="relative w-full max-w-4xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <iframe
              src="https://www.youtube.com/embed/FPtqhHgwi90?autoplay=1"
              title="Miha Bodytec — Électrostimulation"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
            <button
              onClick={() => setShowVideo(false)}
              className="absolute -top-12 right-0 text-white text-sm uppercase tracking-wider"
              aria-label="Fermer"
            >
              ✕ Fermer
            </button>
          </div>
        </div>
      )}

      {/* MARQUEE — services */}

      {/* CTA */}
      <section className="py-16 lg:py-28 px-6 lg:px-10" data-testid="time-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7 text-center lg:text-left order-2 lg:order-1">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-fegray mb-3">
              Vous manquez de temps ?
            </h2>
            <p className="text-lg sm:text-xl lg:text-2xl text-fegreen font-medium mb-8">
              Gagnez du temps avec un coach à domicile !
            </p>

            <div className="mt-4 mb-2">
              <TimerAnimation />
            </div>

            <p className="mt-8 text-fegray leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Avec <span className="font-semibold text-fegreen">Form&apos;Elec</span> et grâce
              à la <span className="font-semibold">technologie d&apos;électrostimulation</span>,
              gagnez du temps dans la pratique d&apos;une activité physique !
            </p>
            <p className="mt-3 text-fegray leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Profitez également de <span className="font-bold text-fegreen">50% de crédit d&apos;impôt</span> sur
              le tarif de votre séance d&apos;électrostimulation à domicile.
            </p>
          </div>

          <div className="lg:col-span-5 flex justify-center order-1 lg:order-2">
            <img src={ROSA_AVATAR} alt="Rosalie — Coach EMS à domicile" className="w-64 sm:w-80 lg:w-full max-w-md h-auto" />
          </div>
        </div>
      </section>

      {/* DEUX UNIVERS — EMS & MASSAGES */}
      <section className="py-16 lg:py-24 px-6 lg:px-10" data-testid="two-universes-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 lg:mb-16">
            <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-3">
              — Deux univers, une coach
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-fegray-dark">
              Trouvez la formule <span className="text-fegreen">qui vous ressemble</span>
            </h2>
            <p className="mt-5 text-fegray max-w-2xl mx-auto leading-relaxed">
              Que vous cherchiez à vous tonifier en 25 minutes ou à relâcher vos tensions
              sous des mains expertes, Rosalie vous accompagne dans deux univers complémentaires.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* === EMS === */}
            <div
              className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#7FB940] to-[#5E9A28] text-white p-8 lg:p-10 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 group"
              data-testid="universe-ems"
            >
              <div className="absolute -right-10 -top-10 w-48 h-48 bg-white/10 rounded-full blur-2xl group-hover:scale-125 transition-transform duration-700" />
              <div className="absolute -right-8 bottom-0 w-32 h-32 bg-white/5 rounded-full blur-xl" />

              <div className="relative">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
                    <Zap className="w-7 h-7 text-white" strokeWidth={2.2} />
                  </div>
                  <span className="text-xs uppercase tracking-[0.25em] font-bold bg-white/15 px-3 py-1 rounded-full">
                    Univers 01
                  </span>
                </div>

                <h3 className="text-3xl lg:text-4xl font-extrabold mb-2">
                  Électrostimulation EMS
                </h3>
                <p className="text-white/90 text-base lg:text-lg font-medium mb-6">
                  Stimuler. Tonifier. Performer.
                </p>

                <p className="text-white/85 leading-relaxed mb-6">
                  La technologie allemande <span className="font-bold">MIHA BODYTEC</span> envoie
                  des impulsions électriques sur 8 groupes musculaires en simultané.
                  <span className="font-bold"> 25 minutes = 4 heures de sport classique.</span>
                </p>

                <ul className="space-y-3 mb-8">
                  {[
                    { icon: Clock, label: "25 min/séance — gain de temps absolu" },
                    { icon: Activity, label: "Travail global, zéro impact articulaire" },
                    { icon: Trophy, label: "Tonification, perte de poids, performance" },
                  ].map((it, i) => {
                    const Ic = it.icon;
                    return (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Ic className="w-4 h-4 text-white" strokeWidth={2.2} />
                        </div>
                        <span className="text-white/95 text-sm">{it.label}</span>
                      </li>
                    );
                  })}
                </ul>

                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/tarifs"
                    className="inline-flex items-center gap-2 bg-white text-[#5E9A28] font-bold px-6 py-3 rounded-full hover:bg-fegray-dark hover:text-white transition-all"
                    data-testid="universe-ems-cta"
                  >
                    Découvrir les tarifs EMS <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/reservation"
                    className="inline-flex items-center gap-2 border-2 border-white/40 text-white font-bold px-6 py-3 rounded-full hover:bg-white/10 transition-all"
                  >
                    Réserver
                  </Link>
                </div>

                <div className="mt-6 pt-6 border-t border-white/15 text-xs text-white/70 flex items-center gap-2">
                  <Sparkles className="w-3 h-3" /> Crédit d&apos;impôt 50% — Services à la personne
                </div>
              </div>
            </div>

            {/* === MASSAGES === */}
            <div
              className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#E6CBAE] to-[#C49B7C] text-[#5A3E27] p-8 lg:p-10 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-1 group"
              data-testid="universe-massage"
            >
              <div className="absolute -right-10 -top-10 w-48 h-48 bg-white/30 rounded-full blur-2xl group-hover:scale-125 transition-transform duration-700" />
              <div className="absolute -right-8 bottom-0 w-32 h-32 bg-white/15 rounded-full blur-xl" />

              <div className="relative">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-14 h-14 rounded-2xl bg-white/40 backdrop-blur-sm flex items-center justify-center">
                    <Leaf className="w-7 h-7 text-[#5A3E27]" strokeWidth={2} />
                  </div>
                  <span className="text-xs uppercase tracking-[0.25em] font-bold bg-white/40 text-[#5A3E27] px-3 py-1 rounded-full">
                    Univers 02
                  </span>
                </div>

                <h3 className="text-3xl lg:text-4xl font-extrabold mb-2">
                  Massages & Bien-être
                </h3>
                <p className="text-[#5A3E27]/80 text-base lg:text-lg font-medium mb-6">
                  Apaiser. Détoxifier. Récupérer.
                </p>

                <p className="text-[#5A3E27]/85 leading-relaxed mb-6">
                  Des soins manuels personnalisés : drainage lymphatique, récupération sportive,
                  palper-rouler, prénatal et post-partum. Un moment de détente profonde,
                  <span className="font-bold"> chez vous, en toute intimité.</span>
                </p>

                <ul className="space-y-3 mb-8">
                  {[
                    { icon: Droplet, label: "Drainage lymphatique — détox & jambes légères" },
                    { icon: Wind, label: "Palper-rouler — anti-cellulite & tonification" },
                    { icon: Baby, label: "Prénatal & post-partum — soins doux & adaptés" },
                  ].map((it, i) => {
                    const Ic = it.icon;
                    return (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-full bg-white/40 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Ic className="w-4 h-4 text-[#5A3E27]" strokeWidth={2.2} />
                        </div>
                        <span className="text-[#5A3E27]/95 text-sm">{it.label}</span>
                      </li>
                    );
                  })}
                </ul>

                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/univers/massages"
                    className="inline-flex items-center gap-2 bg-[#5A3E27] text-white font-bold px-6 py-3 rounded-full hover:bg-[#3D2A1A] transition-all"
                    data-testid="universe-massage-cta"
                  >
                    Découvrir les soins <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/reservation"
                    className="inline-flex items-center gap-2 border-2 border-[#5A3E27]/40 text-[#5A3E27] font-bold px-6 py-3 rounded-full hover:bg-white/30 transition-all"
                  >
                    Réserver
                  </Link>
                </div>

                <div className="mt-6 pt-6 border-t border-[#5A3E27]/20 text-xs text-[#5A3E27]/70 flex items-center gap-2">
                  <Sparkles className="w-3 h-3" /> Crédit d&apos;impôt 50% — Services à la personne
                </div>
              </div>
            </div>
          </div>

          {/* Duo highlight */}
          <div className="mt-8 lg:mt-10 fe-card p-6 lg:p-8 bg-gradient-to-r from-fegray-dark via-[#3a3a3a] to-fegray-dark text-white flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-fegreen to-[#C49B7C] flex items-center justify-center shadow-lg flex-shrink-0">
                <Sparkles className="w-7 h-7 text-white" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-fegreen font-bold mb-1">
                  ★ Le combo signature
                </p>
                <h4 className="text-xl lg:text-2xl font-bold">
                  Duo EMS + Massage récupération
                </h4>
                <p className="text-white/70 text-sm mt-1">
                  Le meilleur des deux univers en une seule séance — 25 min de tonification + 30 min de récupération.
                </p>
              </div>
            </div>
            <Link
              to="/reservation?pack=duo_ems_massage"
              className="btn-green !py-3 !px-6 whitespace-nowrap"
              data-testid="universe-duo-cta"
            >
              {priceOf("duo_ems_massage") || "80€"} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-16 lg:py-24 px-6 lg:px-10" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 lg:mb-16">
            <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-3">
              — Pour chaque objectif
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-fegray-dark">
              Une solution adaptée
            </h2>
            <p className="text-base sm:text-lg text-fegray mt-3 max-w-2xl mx-auto">
              EMS pour tonifier, massages pour relâcher — chaque besoin trouve sa réponse.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              const isMassage = b.universe === "Massages";
              return (
                <div key={i} data-testid={`benefit-card-${i}`} className="fe-card overflow-hidden">
                  <div className="aspect-[16/10] overflow-hidden bg-fegray-light relative">
                    <img src={b.image} alt={b.title} className="w-full h-full object-cover" />
                    <span
                      className={`absolute top-4 left-4 inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full shadow-md ${
                        isMassage ? "bg-[#C49B7C] text-white" : "bg-fegreen text-white"
                      }`}
                    >
                      {isMassage ? <Leaf className="w-3 h-3" /> : <Zap className="w-3 h-3" />}
                      {b.universe}
                    </span>
                  </div>
                  <div className="p-7 lg:p-8">
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isMassage ? "bg-[#F2E0CC]" : "bg-fegreen-light"
                      }`}>
                        <Icon className={`w-6 h-6 ${isMassage ? "text-[#8B6644]" : "text-fegreen"}`} strokeWidth={1.8} />
                      </div>
                      <h3 className="text-xl lg:text-2xl font-bold text-fegray-dark">{b.title}</h3>
                    </div>
                    <p className={`font-medium mb-5 text-sm ${isMassage ? "text-[#8B6644]" : "text-fegreen"}`}>
                      {b.subtitle}
                    </p>
                    <ul className="space-y-2.5">
                      {b.points.map((p, j) => (
                        <li key={j} className="flex items-start gap-3 text-fegray">
                          <Check className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isMassage ? "text-[#C49B7C]" : "text-fegreen"}`} strokeWidth={2.5} />
                          <span className="text-sm">{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONCEPT EMS */}
      <section className="py-16 lg:py-28 px-6 lg:px-10 bg-fegray-light" data-testid="concept-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl overflow-hidden aspect-square">
                <img src={IMG_EMS_HOME} alt="EMS bien-être" className="w-full h-full object-cover" />
              </div>
              <div className="rounded-2xl overflow-hidden aspect-square mt-8">
                <img src={IMG_EMS_SPORT} alt="EMS sport" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-fegray-dark mb-6">
              Le concept d&apos;EMS à domicile
            </h2>
            <p className="text-fegray leading-relaxed mb-4">
              <span className="font-bold text-fegreen">FORM&apos;ELEC</span> propose des séances
              d&apos;EMS (Electro Myo Stimulation) à <span className="font-semibold">domicile</span> avec
              la technologie allemande <span className="font-bold">MIHA BODYTEC</span> déjà
              utilisée depuis des années dans le milieu sportif et médical.
            </p>
            <p className="text-fegray leading-relaxed mb-4">
              C&apos;est une technologie qui permet <span className="font-semibold">un entraînement global et intensif des 8 zones musculaires du corps</span> et
              qui est pratiqué en toute sécurité.
            </p>
            <p className="text-fegray leading-relaxed mb-6">
              Pendant chaque séance, on utilise la contraction volontaire associée à des
              impulsions électriques envoyées par la machine afin d&apos;atteindre les
              fibres profondes des muscles sans stress sur les articulations.
            </p>

            <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm">
              <h3 className="text-lg font-bold text-fegray-dark mb-2">Le but de l&apos;électrostimulation</h3>
              <p className="text-fegray text-sm">
                Augmenter le <span className="font-bold text-fegreen">métabolisme basal</span> (calories
                brûlées au repos) en travaillant sur tous les muscles en profondeur.
              </p>
            </div>

            <Link to="/tarifs" className="btn-green" data-testid="concept-cta-btn">
              Découvrir l&apos;offre EMS <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CREDIT IMPOT BANNER */}
      <section className="py-16 lg:py-28 px-6 lg:px-10" data-testid="credit-banner">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-5 flex justify-center order-2 lg:order-1">
            <img src={SAP_LOGO} alt="Services à la personne — Crédit d'impôt 50%" className="w-56 sm:w-72 lg:w-full max-w-sm" />
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2 text-center lg:text-left">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-fegray-dark mb-2">
              Crédit d&apos;impôts :
            </h2>
            <p className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-fegreen mb-6">Déduction de 50%</p>
            <p className="text-fegray leading-relaxed mb-4">
              L&apos;<span className="font-bold">Avance immédiate</span> est un
              service <span className="font-semibold">optionnel et gratuit</span> qui
              permet la déduction immédiate de votre crédit d&apos;impôt de 50% lors du
              paiement de votre facture à votre organisme prestataire.
            </p>
            <p className="text-fegray leading-relaxed mb-8">
              Concrètement, pour une dépense de <span className="font-bold">200€</span> de
              séances d&apos;électrostimulation, l&apos;Urssaf ne prélèvera plus que les
              <span className="font-bold text-fegreen"> 100€</span> de reste à charge.
            </p>
            <Link to="/credit-impot" className="btn-outline-green" data-testid="credit-banner-cta">
              En savoir + sur le crédit d&apos;impôts <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 lg:py-28 px-6 lg:px-10 bg-fegray-light" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 lg:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-fegray-dark mb-3">
              Avis clients
            </h2>
            <p className="text-lg sm:text-xl text-fegreen font-medium">
              Électrostimulation Istres
            </p>
            <div className="inline-flex flex-wrap items-center justify-center gap-2 sm:gap-3 mt-6 bg-white rounded-full shadow-md px-4 sm:px-6 py-3">
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 sm:w-5 h-4 sm:h-5 fill-fegreen text-fegreen" />
                ))}
              </div>
              <span className="font-bold text-fegray-dark">4.9</span>
              <span className="text-xs sm:text-sm text-fegray">— Sur 34 avis</span>
              <span className="text-xs text-fegray border-l border-gray-200 pl-3 ml-1 uppercase tracking-wider">
                Google
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.slice(0, 6).map((t, i) => (
              <div key={t.id} className="fe-card p-7" data-testid={`testimonial-${i}`}>
                <Quote className="w-7 h-7 text-fegreen mb-4" />
                <div className="flex gap-0.5 mb-3">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-fegreen text-fegreen" />
                  ))}
                </div>
                <p className="text-fegray text-sm leading-relaxed mb-6">&ldquo;{t.text}&rdquo;</p>
                <div className="flex justify-between items-end pt-4 border-t border-gray-100">
                  <div>
                    <div className="font-semibold text-fegray-dark text-sm">{t.author}</div>
                    <div className="text-xs text-fegray/60 mt-0.5">{t.years_ago}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Google reviews CTA */}
          <div className="text-center mt-10 lg:mt-12">
            <a
              href="https://www.google.com/search?q=Form%27elec+Istres+avis"
              target="_blank"
              rel="noreferrer"
              data-testid="google-reviews-link"
              className="inline-flex items-center gap-3 bg-white hover:bg-fegray-dark hover:text-white text-fegray-dark font-bold px-7 py-4 rounded-full shadow-md hover:shadow-xl transition-all border border-gray-100 group"
            >
              <span className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
              </span>
              Voir tous nos avis sur Google
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20 px-6 lg:px-10 bg-fegreen text-white">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Prête à booster votre bien-être ?
          </h2>
          <p className="text-base sm:text-lg text-white/90 mb-10 max-w-2xl mx-auto">
            Première séance découverte à domicile. Bilan personnalisé.
            Crédit d&apos;impôt 50% applicable dès la première facture.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/reservation" className="btn-dark bg-white !text-fegreen hover:!bg-fegray-dark hover:!text-white" data-testid="cta-reserve-btn">
              Réserver ma séance <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="btn-outline-green !text-white !border-white hover:!bg-white hover:!text-fegreen" data-testid="cta-contact-btn">
              Me contacter
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}


function Pillar({ accent, accentSoft, icon: Icon, tag, title, tagline, bullets, cta, ctaLink, testid }) {
  return (
    <Link
      to={ctaLink}
      data-testid={testid}
      className="group relative rounded-2xl p-5 sm:p-6 backdrop-blur-md border border-white/15 hover:border-white/30 transition-all hover:-translate-y-1 hover:shadow-2xl overflow-hidden block"
      style={{
        background: `linear-gradient(135deg, ${accentSoft} 0%, rgba(0,0,0,0.35) 100%)`,
      }}
    >
      <div
        className="absolute -top-12 -right-12 w-32 h-32 rounded-full blur-3xl opacity-50 group-hover:opacity-80 transition-opacity"
        style={{ background: accent }}
      />
      <div className="relative flex items-start gap-3 mb-3">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: accent }}
        >
          <Icon className="w-5 h-5 text-white" strokeWidth={2.2} fill={accent === "#84C342" ? accent : "none"} />
        </div>
        <div className="flex-1 text-left">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: accent }}>
            {tag}
          </div>
          <div className="text-lg sm:text-xl font-bold text-white leading-tight mt-0.5">{title}</div>
        </div>
      </div>
      <p
        className="text-sm font-semibold mb-3 text-left"
        style={{ color: accent }}
      >
        {tagline}
      </p>
      <ul className="space-y-1.5 mb-4 text-left">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-xs sm:text-sm text-white/85">
            <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={2.5} style={{ color: accent }} />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold group-hover:gap-2.5 transition-all" style={{ color: accent }}>
        {cta} <ArrowRight className="w-3.5 h-3.5" />
      </span>
    </Link>
  );
}
